import { Link } from "react-router-dom";
import { PageHero, CTABand } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { FileText, ArrowRight, CheckCircle2 } from "lucide-react";

const GUIDES = [
  { title: "Commercial Relocation Checklist", desc: "A 90-day countdown covering IT, furniture, communications, and move-week logistics for office moves of any size.", tag: "Office Moves" },
  { title: "Lab & Biotech Facility Migration Matrix", desc: "Chain-of-custody planning, cold-chain requirements, and EH&S coordination for regulated lab transitions.", tag: "Biotech & Labs" },
  { title: "Office Space Planning Guide", desc: "Test fits, headcount modeling, and furniture standards that make your next space work harder from day one.", tag: "Space Planning" },
];

const TIPS = [
  "Start planning commercial moves 90–120 days before your target date.",
  "Assign one internal move champion per department to coordinate with our crew leads.",
  "Purge and shred before you pack — moving paper you don't need costs twice.",
  "Photograph workstation setups before disconnect for faster day-one reassembly.",
  "Reserve freight elevators and loading docks at both locations in writing.",
];

export default function Resources() {
  return (
    <main data-testid="resources-page">
      <PageHero
        eyebrow="Resources"
        title="Move smarter, not harder"
        subtitle="Planning guides and hard-won expertise from hundreds of commercial and residential relocations."
      />
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <div className="grid md:grid-cols-3 gap-5 mb-20">
          {GUIDES.map((g, i) => (
            <Reveal key={g.title} delay={i * 0.08}
              className="group bg-white border border-slate-200 rounded-md p-8 flex flex-col hover:border-brand hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand mb-4">{g.tag}</span>
              <FileText size={28} className="text-navy mb-4" />
              <h3 className="font-display font-semibold text-lg text-navy mb-2">{g.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed flex-1">{g.desc}</p>
              <Link to="/contact" data-testid={`resource-request-${i}`}
                className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-brand hover:text-brand-hover transition-colors">
                Request This Guide <ArrowRight size={14} />
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-brand mb-3">Pro Tips</p>
          <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl text-navy mb-10">
            Five rules of a smooth move
          </h2>
        </Reveal>
        <div className="max-w-3xl space-y-4">
          {TIPS.map((t, i) => (
            <Reveal key={i} delay={i * 0.06} className="flex items-start gap-4 bg-white border border-slate-200 rounded-md p-5">
              <CheckCircle2 size={20} className="text-brand mt-0.5 shrink-0" />
              <p className="text-sm text-slate-700 leading-relaxed">{t}</p>
            </Reveal>
          ))}
        </div>
      </section>
      <CTABand />
    </main>
  );
}
